import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts';
import { Subject, Subscription, switchMap, takeUntil, of } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { commaSeparatedValue, formatDecimalWithCommas } from '../../utils/format.utils';
import { buildTreemapTooltipFormatter } from '../../utils/chart.utils';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import {
  buildCategoryFilterData,
  buildLegendSyncData,
  matchValue,
  MatchMode,
} from '../../utils/filter.utils';
import {
  getSeriesColor,
  ColorConfig,
} from '../../utils/color.utils';

@Component({
  selector: 'app-tree-map-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './treemap-chart.component.html',
  styleUrl: './treemap-chart.component.scss',
})
export class TreemapChartComponent implements AfterViewInit, OnDestroy {
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected reflowSub?: Subscription;
  @Input() data!: any;
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }

  @ViewChild('legendContainer')
  protected legendContainerRef!: ElementRef<HTMLDivElement>;
  public showLeftArrow = false;
  public showRightArrow = false;
  protected unSubscribe$: Subject<void> = new Subject();

  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }

  protected _widgetId!: string;
  protected myChart!: echarts.ECharts;
  protected filterSub!: Subscription;
  protected originalTreemapData: any;
  protected selectedCustomer: string | null = null;
  protected selectedCustomers: string[] = [];
  protected allParentNames: string[] = [];
  public hasError = false;
  public errorMessage = '';
  public legendItems: { name: string; color: string }[] = [];
  public activeLegend: string | null = null;
  public activeLegends: string[] = [];

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) {}

  ngAfterViewChecked(): void {
    setTimeout(() => this.updateArrowVisibility(), 0);
  }

  ngAfterViewInit(): void {
    // Initial load
    this.loadAndRenderChart()
      ?.pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.handleResponse(res),
        error: () => this.handleError(),
      });

    // Reload when filterListener targets this widget
    this.filterSub = this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          // Handle legend sync (receiving cross-filter from other charts)
          if (filters?.legendSync) {
            this.handleExternalLegendSync(filters);
            if (filters.isCategoryFilter) {
              return this.loadAndRenderChart(filters) || of(null);
            }
            return of(null);
          }

          const customer = filters?.requestParams?.['customer_name']?.[0];

          // sync local state with filter
          this.selectedCustomer = customer || null;

          if (this.selectedCustomer) {
            this.highlightNode(this.selectedCustomer);
          } else {
            this.resetHighlight();
          }

          return this.loadAndRenderChart(filters) || of(null);
        }),
      )
      .subscribe({
        next: (res) => {
          if (!res) return;
          this.handleResponse(res);
        },
        error: () => this.handleError(),
      });

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });
  }

  private handleResponse(res: any): void {
    const treeData = this.extractTreeData(res);
    this.originalTreemapData = JSON.parse(JSON.stringify(treeData));

    const isEmpty = !treeData || (Array.isArray(treeData) && treeData.length === 0);

    if (isEmpty) {
      this.hasError = true;
      this.errorMessage = 'No data found for the selected filters.';
      this.myChart.clear();
    } else if (this.hasOnlyNonPositiveValues(treeData)) {
      this.hasError = true;
      this.errorMessage = 'Data (-ve or zero) is not appropriate for chart.';
    } else {
      this.hasError = false;
      this.renderChart(treeData);
    }

    this.myChart.hideLoading();
  }

  private handleError(): void {
    this.hasError = true;
    this.errorMessage = 'No Data available for the chart.';
    this.myChart?.clear();
    this.myChart.hideLoading();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateArrowVisibility();
  }

  public scrollLegend(direction: 'left' | 'right'): void {
    const element = this.legendContainerRef.nativeElement;
    const scrollAmount = element.clientWidth * 0.8;

    element.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }

  public updateArrowVisibility(): void {
    if (!this.legendContainerRef) return;

    const element = this.legendContainerRef.nativeElement;
    const scrollLeft = element.scrollLeft;
    const scrollWidth = element.scrollWidth;
    const clientWidth = element.clientWidth;

    this.showLeftArrow = scrollLeft > 0;
    this.showRightArrow = scrollWidth - scrollLeft - clientWidth > 1;
  }

  protected loadAndRenderChart(filters?: any): any {
    const chartDom = document.getElementById(this.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    return this.configService
      .getConfig(this.data?.apiUrl, this.data, {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false,
      })
      .pipe(takeUntil(this.unSubscribe$));
  }

  protected renderChart(treeData: any): void {
    // Add parent label logic
    treeData.forEach((parent: any) => {
      const maxChild = parent.children?.reduce((a: any, b: any) => (a.value > b.value ? a : b), {});
      if (maxChild) {
        maxChild.upperLabel = parent.name;
      }
    });

    const colors = [
      '#5470c6',
      '#91cc75',
      '#fac858',
      '#ee6666',
      '#73c0de',
      '#3ba272',
      '#fc8452',
      '#9a60b4',
      '#2196F3',
      '#ea7ccc',
      '#4CAF50',
      '#FFC107',
      '#E91E63',
      '#00BCD4',
      '#8BC34A',
      '#FF9800',
      '#9C27B0',
      '#009688',
      '#F44336',
      '#673AB7',
      '#CDDC39',
      '#FF5722',
      '#607D8B',
      '#795548',
      '#03A9F4',
    ];

    treeData.sort((a: { value: number }, b: { value: number }) => b.value - a.value);
    // Store parent names for cross-filtering
    this.allParentNames = treeData.map((item: any) => item.name);

    // Color consistency support
    const config = this.data?.chartConfig || {};
    const colorConfig: ColorConfig = {
      colorMap: config.colorMap || {},
      topNColorsByStackPosition: config.topNColorsByStackPosition || [],
    };

    this.legendItems = [];
    treeData.forEach((item: any, index: number) => {
      let color = getSeriesColor(index, item.name, colorConfig) || colors[index % colors.length];

      if (this.data?.enableColorConsistency) {
        const globalColor = this.chartFilterHandler.getItemColor(item.name);
        if (globalColor) {
          color = globalColor;
        } else {
          this.chartFilterHandler.registerItemColor(item.name, color);
        }
      }

      item.itemStyle = {
        color: color,
      };
      this.legendItems.push({
        name: item.name,
        color: color,
      });
    });

    const option: echarts.EChartsOption = {
      legend: {
        show: false,
      },
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const treemapFormatter = buildTreemapTooltipFormatter(tooltipCfg);
          if (treemapFormatter) return treemapFormatter;

          return (params: any) => {
            const path = params.treePathInfo;
            const parentName = path?.[1]?.name || '';
            const childName = params.name || '';
            const value = params.value ?? '';
            const currentSales = params.data?.CurrentSales ?? null;

            const formattedValue = this.data.useCommaStat ? formatDecimalWithCommas(value) : value;
            const formattedSales = this.data.useCommaStat
              ? formatDecimalWithCommas(currentSales)
              : currentSales;

            const tooltipLabels = this.data.tooltipLabels || {};
            const parentLabel = tooltipLabels.parent || 'Parent';
            const childLabel = tooltipLabels.child || 'Child';
            const valueLabel = tooltipLabels.value || 'Value';
            const salesLabel = tooltipLabels.sales || 'Current Sales';

            return `
            ${parentLabel}: ${parentName}<br/>
            ${childLabel}: ${childName}<br/>
            ${valueLabel}: ${formattedValue}<br/>
            ${salesLabel}: ${formattedSales}
          `;
          };
        })(),
      },
      series: [
        {
          type: 'treemap',
          data: treeData,
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          label: {
            show: true,
            formatter: (params: any) => {
              const { upperLabel, name } = params.data;
              if (upperLabel) return `{parent|${upperLabel}}\n{child|${name}}`;
              return `{child|${name}}`;
            },
            rich: {
              parent: {
                fontSize: 12,
                fontWeight: 'bold',
                padding: [0, 0, 0, 5],
              },
              child: { fontSize: 10, padding: [0, 0, 5, 5] },
            },
          },
          itemStyle: {
            borderColor: '#fff',
          },
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        },
      ],
    };

    this.myChart.setOption(option);
    setTimeout(() => this.myChart.hideLoading(), 800);

    if (this.data?.filterKey) {
      this.setupChartClickEvent();
    }
  }

  protected hasOnlyNonPositiveValues(treeData: any[]): boolean {
    const allValues: number[] = [];

    const traverse = (nodes: any[]) => {
      for (const node of nodes) {
        if (typeof node.value === 'number') {
          allValues.push(node.value);
        }
        if (node.children) {
          traverse(node.children);
        }
      }
    };

    traverse(treeData);
    return allValues.length > 0 && allValues.every((val) => val <= 0);
  }

  protected setupChartClickEvent(): void {
    this.myChart.off('click');

    this.myChart.on('click', (params: any) => {
      const segment = params?.treePathInfo?.[1]?.name;
      const customer = params?.name;

      if (!segment || !customer) return;

      if (this.data?.isMultiSelect) {
        // Multiselect: toggle customer in/out of selection
        const pos = this.selectedCustomers.indexOf(customer);
        if (pos === -1) {
          this.selectedCustomers = [...this.selectedCustomers, customer];
        } else {
          this.selectedCustomers = this.selectedCustomers.filter(c => c !== customer);
        }
      } else {
        // Single-select: toggle same customer off, or replace selection
        if (this.selectedCustomers.length === 1 && this.selectedCustomers[0] === customer) {
          this.selectedCustomers = [];
        } else {
          this.selectedCustomers = [customer];
        }
      }

      this.selectedCustomer = this.selectedCustomers.length > 0 ? this.selectedCustomers[this.selectedCustomers.length - 1] : null;

      if (this.selectedCustomers.length === 0) {
        this.resetHighlight();

        const payload = { application_id: segment, customer_name: customer };
        this.chartFilterHandler.updateFilterSelection(this.data, payload);

        if (this.data?.enableLabelClick) {
          this.broadcastCategoryFilter([]);
        }
      } else {
        this.highlightNodes(this.selectedCustomers);

        const payload = { application_id: segment, customer_name: customer };
        this.chartFilterHandler.updateFilterSelection(this.data, payload);

        if (this.data?.enableLabelClick) {
          this.broadcastCategoryFilter(this.selectedCustomers);
        }
      }
    });
  }

  protected broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig =
      this.data?.labelListener || this.data?.legendListener ||
      this.data?.filterListener || this.data?.filterListner;

    // Silently update filter state so the API call carries the labelKey param
    const labelKey = this.data?.labelKey || this.data?.filterKey;
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(
          labelKey, categories, false, this.data.widgetId, false, listenerConfig,
        );
      } else {
        this.globalFilterService.deleteKey(labelKey, false);
      }
    }

    // Single emission: legendSync enriched with current filter state
    const filterData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this.data.widgetId, listenerConfig, this.data?.labelKey || this.data?.legendKey || this.data?.filterKey),
    };
    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  public toggleLegend(legendName: string): void {
    if (!this.data?.enableLegendClick) return;

    if (this.data?.isMultiSelect) {
      // Multiselect: toggle legend in/out of selection
      const pos = this.activeLegends.indexOf(legendName);
      if (pos === -1) {
        this.activeLegends = [...this.activeLegends, legendName];
      } else {
        this.activeLegends = this.activeLegends.filter(l => l !== legendName);
      }
    } else {
      // Single-select: toggle same legend off, or replace selection
      if (this.activeLegends.length === 1 && this.activeLegends[0] === legendName) {
        this.activeLegends = [];
      } else {
        this.activeLegends = [legendName];
      }
    }

    // Keep legacy activeLegend in sync
    this.activeLegend = this.activeLegends.length > 0 ? this.activeLegends[this.activeLegends.length - 1] : null;

    const legendListener = this.data?.legendListener || this.data?.legendListner;
    const legendFilterKey = this.data?.legendKey;
    const cloned = legendListener
      ? { ...this.data, filterListener: legendListener, ...(legendFilterKey ? { filterKey: legendFilterKey } : {}) }
      : this.data;

    if (this.activeLegends.length === 0) {
      this.resetHighlight();
      this.broadcastLegendSync(null);
      try { this.chartFilterHandler.updateFilterSelection(cloned, []); } catch (e) {}
    } else {
      this.highlightParents(this.activeLegends);
      this.broadcastLegendSync(this.activeLegends.length === 1 ? this.activeLegends[0] : this.activeLegends[this.activeLegends.length - 1]);
      try {
        this.chartFilterHandler.updateFilterSelection(cloned, this.activeLegends.length === 1 ? this.activeLegends[0] : this.activeLegends);
      } catch (e) {}
    }
  }

  protected broadcastLegendSync(legendName: string | null): void {
    const listenerConfig =
      this.data?.legendListener || this.data?.legendListner ||
      this.data?.filterListener || this.data?.filterListner;

    const selectedIndices = legendName ? [this.allParentNames.indexOf(legendName)] : [];
    const selectedLegends = legendName ? [legendName] : [];
    const legendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;

    const filterData = buildLegendSyncData(
      selectedIndices, selectedLegends, this.data.widgetId, listenerConfig,
      { legendKey },
    );
    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  protected handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this.data?.widgetId) return;
    if (!this.myChart) return;

    // Reset if empty selection — but only if source shares the same legendKey.
    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      const myLegendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;
      const sourceLegendKey = filters.legendKey;
      if (!sourceLegendKey || sourceLegendKey === myLegendKey) {
        this.resetHighlight();
      }
      return;
    }

    if (filters.isCategoryFilter && filters.selectedLegends?.length > 0) {
      this.handleCategoryFilter(filters.selectedLegends, filters.sourceWidgetId);
    } else if (filters.selectedLegends?.length > 0) {
      // Try to match by name against parent names
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingParent = this.allParentNames.find((name: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(name, legend, matchMode)),
      );
      if (matchingParent) {
        this.highlightParent(matchingParent);
      }
      // No match means different data domains — preserve current selection
    }
  }

  protected handleCategoryFilter(selectedLegends: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data?.widgetId) return;
    if (!this.myChart || !this.originalTreemapData) return;
    if (selectedLegends.length === 0) { this.resetHighlight(); return; }

    const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;

    // Try matching against parent names
    const matchingParent = this.allParentNames.find((name: string) =>
      selectedLegends.some((legend: string) => matchValue(name, legend, matchMode)),
    );

    if (matchingParent) {
      this.highlightParent(matchingParent);
    } else {
      // Try matching against child names
      let matchedChild: string | null = null;
      for (const parent of this.originalTreemapData) {
        if (parent.children) {
          for (const child of parent.children) {
            if (selectedLegends.some((legend: string) => matchValue(child.name, legend, matchMode))) {
              matchedChild = child.name;
              break;
            }
          }
          if (matchedChild) break;
        }
      }
      if (matchedChild) {
        this.highlightNode(matchedChild);
      }
      // No match means different data domains — preserve current selection
    }
  }

  protected highlightParent(parentName: string): void {
    if (!this.originalTreemapData) return;
    const updated = JSON.parse(JSON.stringify(this.originalTreemapData));

    updated.forEach((parent: any) => {
      const isMatch = parent.name === parentName;
      if (parent.children) {
        parent.children.forEach((child: any) => {
          child.itemStyle = { opacity: isMatch ? 1 : 0.3 };
        });
      }
      parent.itemStyle = { ...(parent.itemStyle || {}), opacity: isMatch ? 1 : 0.3 };
    });

    this.myChart.setOption({ series: [{ type: 'treemap', data: updated }] });
  }

  protected highlightParents(parentNames: string[]): void {
    if (!this.originalTreemapData) return;
    const updated = JSON.parse(JSON.stringify(this.originalTreemapData));

    updated.forEach((parent: any) => {
      const isMatch = parentNames.includes(parent.name);
      if (parent.children) {
        parent.children.forEach((child: any) => {
          child.itemStyle = { opacity: isMatch ? 1 : 0.3 };
        });
      }
      parent.itemStyle = { ...(parent.itemStyle || {}), opacity: isMatch ? 1 : 0.3 };
    });

    this.myChart.setOption({ series: [{ type: 'treemap', data: updated }] });
  }

  protected highlightNode(name: string): void {
    const updated = JSON.parse(JSON.stringify(this.originalTreemapData));

    updated.forEach((parent: any) => {
      parent.children?.forEach((child: any) => {
        child.itemStyle = { opacity: child.name === name ? 1 : 0.3 };
      });
    });

    this.myChart.setOption({ series: [{ type: 'treemap', data: updated }] });
  }

  protected highlightNodes(names: string[]): void {
    const updated = JSON.parse(JSON.stringify(this.originalTreemapData));

    updated.forEach((parent: any) => {
      parent.children?.forEach((child: any) => {
        child.itemStyle = { opacity: names.includes(child.name) ? 1 : 0.3 };
      });
    });

    this.myChart.setOption({ series: [{ type: 'treemap', data: updated }] });
  }

  protected resetHighlight(): void {
    this.selectedCustomer = null;

    if (!this.originalTreemapData) {
      return;
    }

    const reset = structuredClone(this.originalTreemapData);
    reset.forEach((parent: any) => {
      parent.children?.forEach((child: any) => {
        child.itemStyle = { opacity: 1 };
      });
    });

    this.myChart.setOption({ series: [{ type: 'treemap', data: reset }] });
  }

  protected extractTreeData(res: any): any[] {
    const dataKey = this.data?.dataKey;

    // New platform format: res[dataKey] is the tree array directly
    if (dataKey && Array.isArray(res?.[dataKey])) {
      return res[dataKey];
    }

    // Old format: use series path to resolve (e.g. "data" → res.data)
    if (this.data?.series) {
      return this.resolvePath(res, this.data.series) || [];
    }

    // Direct format: res itself is an array
    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  protected resolvePath(obj: any, path: string): any {
    try {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    } catch {
      return [];
    }
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.filterSub?.unsubscribe();
    this.myChart?.dispose();
  }
}